import Axios from 'axios';
import VueCookies from 'vue-cookies';
import { createToast } from 'mosha-vue-toastify';
import { getMentionsAnalytics as getMentionsAnalyticsFromAnalyzer } from './analyzerApi';

// En dev uniquement (localhost) : si le backend est sur une autre origine (ex. IP), forcer l'URL relative
// pour que les requêtes passent par le proxy → cookies HttpOnly OK. En prod (autre host), garder VUE_APP_BACKEND_URL.
let APIBaseURL = process.env.VUE_APP_BACKEND_URL || '';
const isLocalhost = typeof window !== 'undefined' && /^localhost$|^127\.0\.0\.1$/.test(window.location.hostname);
if (isLocalhost && APIBaseURL.startsWith('http') && !APIBaseURL.startsWith(window.location.origin)) {
  APIBaseURL = '/api/v1';
}

const adapter = Axios.create({
  baseURL: APIBaseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // envoie les cookies HttpOnly automatiquement
});

const badgeGeneratorAdapter = Axios.create({
  baseURL: process.env.VUE_APP_BADGE_GENERATOR_URL,
  headers: {
    'Content-Type': 'application/json',
    Authorization: process.env.VUE_APP_BADGE_GENERATOR_KEY,
  },
});

let authRedirectInProgress = false;

adapter.interceptors.request.use((config) => {
  return config;
});

adapter.interceptors.response.use(
  (response) => {
    eachRecursive(response.data);
    return response;
  },
  (error) => {
    const suppressGlobalErrorToast = Boolean(error.config?.suppressGlobalErrorToast);
    if (error.response) {
      if (error.response.status >= 500 && !suppressGlobalErrorToast) {
        createToast(`Server error, please try again later`, {
          position: 'bottom-center',
          hideProgressBar: true,
          timeout: 3000,
          transition: 'slide',
          toastBackgroundColor: '#ff4545',
        });
      }

      // check if authentication error — le serveur a déjà effacé les cookies HttpOnly
      if (error.response.data.code === 3000) {
        // /status/version est un endpoint d'affichage : ne pas forcer un logout global dessus.
        if (error.config?.url?.includes('/status/version')) {
          return Promise.reject(error);
        }

        if (authRedirectInProgress) {
          return Promise.reject(error);
        }
        authRedirectInProgress = true;

        VueCookies.remove('adminToken');
        VueCookies.remove('adminProfile');
        // Importer le store dynamiquement pour éviter les imports circulaires
        import('../store').then(({ default: store }) => {
          store.commit('clearSessionInfo');
        }).catch(() => {});
        window.location.href = '/signin';
      }

      // check if subscription required error
      if (error.response.data.code === 4000) {
        const event = new CustomEvent('openSubscriptionFlow');
        window.dispatchEvent(event);
      }
    } else if (error.request) {
      // Erreur réseau (pas de réponse du serveur)
      console.error('Network error - No response received:', error.message);
      if (!suppressGlobalErrorToast) {
        createToast(`Impossible de joindre le serveur. Vérifiez votre connexion réseau.`, {
          position: 'bottom-center',
          hideProgressBar: true,
          timeout: 4000,
          transition: 'slide',
          toastBackgroundColor: '#ff4545',
        });
      }
    } else if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
      // Erreur réseau spécifique
      console.error('Network Error:', error.message, 'Code:', error.code);
      
      let errorMessage = 'Erreur réseau. Vérifiez votre connexion et que le serveur est accessible.';
      
      // Message plus spécifique pour ERR_CONNECTION_REFUSED
      if (error.code === 'ERR_NETWORK' && (error.message.includes('ERR_CONNECTION_REFUSED') || error.message.includes('Connection refused'))) {
        errorMessage = 'Le serveur backend n\'est pas accessible. Vérifiez qu\'il est démarré et que l\'URL est correcte.';
      }
      
      createToast(errorMessage, {
        position: 'bottom-center',
        hideProgressBar: true,
        timeout: 5000, // Augmenter à 5 secondes pour ce type d'erreur
        transition: 'slide',
        toastBackgroundColor: '#ff4545',
      });
    } else {
      console.error('Error setting up request:', error.message);
    }
    return Promise.reject(error);
  }
);

// badgeGeneratorAdapter : auth via clé statique VUE_APP_BADGE_GENERATOR_KEY uniquement
// (token est maintenant HttpOnly et inaccessible en JS)

function eachRecursive(obj) {
  for (var k in obj) {
    if (typeof obj[k] == 'object' && obj[k] !== null) eachRecursive(obj[k]);
    // Ne pas remplacer null par '' : Number('') === 0 et casse les champs API optionnels
    // (ex. quotas illimités = null → affichage 0/0 sur ManageTemplates).
  }
}

async function updateIssuer(body, file) {
  const formData = new FormData();
  if (file) {
    formData.append('file', file);
  }
  for (const key in body) {
    if (body[key] !== null && body[key] !== undefined) formData.append(key, body[key]);
  }

  return adapter.put('/managers', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

async function createIssuer(body, file) {
  const formData = new FormData();
  formData.append('file', file);
  for (const key in body) {
    const value = body[key];
    if (value === null || value === undefined) continue;
    if (Array.isArray(value)) {
      if (!value.length) continue;
      value.forEach((item) => formData.append(key, item));
      continue;
    }
    formData.append(key, value);
  }

  return adapter.post('/managers', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

async function switchIssuerFromPrekey(body, file) {
  const formData = new FormData();
  if (file) {
    formData.append('file', file);
  }
  for (const key in body) {
    if (body[key] === null || body[key] === undefined) continue;
    const value = body[key];
    formData.append(
      key,
      typeof value === 'boolean' ? (value ? 'true' : 'false') : value
    );
  }

  return adapter.post('/managers/switch-prekey', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

async function deleteIssuer() {
  return adapter.delete('/managers');
}

async function getIssuer() {
  return adapter.get('/managers');
}

async function getSubIssuers(pageNumber, pageSize, search, onlyValidated) {
  return adapter.get('/managers/sub', {
    params: {
      pageNumber,
      pageSize,
      search: encodeURIComponent(search),
      onlyValidated,
    },
  });
}

async function isIssuerRegistered(did) {
  return adapter.get(`/managers/registered/${did}`);
}

async function switchSubIssuer(did) {
  return adapter.post(`/auth/switch`, { did });
}

async function getIssuerTemplates() {
  return adapter.get('/managers/templates');
}

async function getIssuerTypeTemplates(type) {
  return adapter.get(`/managers/templates/type/${type}`);
}

async function getIssuerTemplate(id) {
  return adapter.get(`/managers/templates/${id}`);
}

async function updateTemplate(body, file, type) {
  const formData = new FormData();
  if (file) {
    formData.append('file', file);
  }

  for (const key in body) {
    if (body[key] !== null) {
      formData.append(key, body[key]);
    }
  }

  return adapter.put(`/managers/templates/${type.toLowerCase()}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

async function publishTemplate(id) {
  return adapter.post(`/managers/templates/publish/${id}`);
}

async function deactivateTemplate(id) {
  return adapter.post(`/managers/templates/deactivate/${id}`);
}

async function updateTemplateStatus(id, status) {
  return adapter.post(`/managers/templates/status/${id}`, { status });
}

async function deleteTemplate(id) {
  return adapter.delete(`/managers/templates/${id}`);
}

async function createTemplate(body, file, type) {
  const formData = new FormData();

  if (file) {
    formData.append('file', file);
  }

  for (const key in body) {
    if (body[key] !== null) {
      formData.append(key, body[key]);
    }
  }

  return adapter.post(`/managers/templates/${type.toLowerCase()}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

async function createSession(body) {
  return adapter.post(`/sessions`, body);
}

async function sendOpenSessionByEmail(body) {
  return adapter.post(`/sessions/send`, body);
}

async function getSendStatus(token) {
  return adapter.get(`/sessions/send-status`, { params: { token } });
}

async function getEmailRateLimitStatus() {
  return adapter.get(`/sessions/email-rate-status`);
}

async function updateSessionNote(body) {
  return adapter.put(`/sessions/note`, body);
}

async function createSessionBuilder(body) {
  return adapter.post(`/sessions/builders`, body);
}

async function retriggerExpiredSession(body) {
  return adapter.put(`/sessions/resend`, body);
}

async function getCommunityChallenges() {
  return adapter.get(`/managers/challenges`);
}

async function createCommunityChallenge(body) {
  return adapter.post(`/managers/challenges`, body);
}

async function updateCommunityChallenge(body) {
  return adapter.put(`/managers/challenges`, body);
}

async function deleteCommunityChallenge(challengeId) {
  return adapter.delete(`/managers/challenges/${challengeId}`);
}

async function getCommunityQuests() {
  return adapter.get(`/managers/quests`);
}

async function getQuestChallenges(questId) {
  return adapter.get(`/managers/quests/${questId}/challenges`);
}

async function getQuestParticipants(
  questId,
  pageNumber,
  pageSize,
  search,
  sort,
  order
) {
  return adapter.get(`/managers/quests/${questId}/participants`, {
    params: {
      pageNumber,
      pageSize,
      search: encodeURIComponent(search || ''),
      sort,
      order,
    },
  });
}

async function createCommunityQuest(body) {
  return adapter.post(`/managers/quests`, body);
}

async function updateCommunityQuest(questId, body) {
  return adapter.put(`/managers/quests/${questId}`, body);
}

async function deleteCommunityQuest(questId) {
  return adapter.delete(`/managers/quests/${questId}`);
}

async function logout() {
  return adapter.post('/auth/logout');
}

async function activateSession(token) {
  return adapter.post('/auth/activate', { token });
}

async function promoteSession() {
  return adapter.post('/auth/promote');
}

async function adminRestore(adminToken) {
  return adapter.post('/auth/admin-restore', { adminToken });
}

async function sendManagerMagicAuth(body) {
  return adapter.post('/auth/manager-magic-link', body);
}

async function leadBrandAudit(body) {
  return adapter.post('/public/lead-brand-audit', body);
}

async function getLeadBrandAuditStatus(auditId) {
  return adapter.get(`/public/lead-brand-audit/${encodeURIComponent(auditId)}`, {
    // Polling can survive a short proxy/network interruption. Avoid displaying
    // a global red toast on every retry while the loading screen stays active.
    suppressGlobalErrorToast: true,
  });
}

async function subscribeNewsletter(body) {
  return adapter.post('/public/newsletter-subscribe', body);
}

async function getBlogArticles(params) {
  return adapter.get('/public/blog/articles', { params });
}

async function getBlogArticle(id, params) {
  return adapter.get(`/public/blog/articles/${encodeURIComponent(id)}`, { params });
}

async function getBlogArticleBySlug(slug, params) {
  return adapter.get(`/public/blog/slug/${encodeURIComponent(slug)}`, { params });
}

async function getPressPage(params) {
  return adapter.get('/public/press', { params });
}

async function resendExpiredManagerMagicAuth(code, locale) {
  return adapter.post('/auth/manager-magic-link/resend-expired', { code, locale });
}

async function verifyManagerMagicAuth(code) {
  return adapter.post('/auth/manager-magic-link/verify', { code });
}

async function getPasskeyLoginOptions(email) {
  return adapter.post('/auth/passkey/login/options', { email });
}

async function verifyPasskeyLogin(body) {
  return adapter.post('/auth/passkey/login/verify', body);
}

async function getPasskeyRegisterOptions() {
  return adapter.post('/auth/passkey/register/options', {});
}

async function verifyPasskeyRegister(body) {
  return adapter.post('/auth/passkey/register/verify', body);
}

async function getPasskeyCredentials() {
  return adapter.get('/auth/passkey/credentials');
}

async function deletePasskeyCredential(credentialId) {
  return adapter.delete(`/auth/passkey/credentials/${credentialId}`);
}

async function updatePasskeyCredentialName(credentialId, name) {
  return adapter.patch(`/auth/passkey/credentials/${credentialId}`, { name });
}

async function finalizeMagicAuth(code, did) {
  return adapter.post('/auth/manager-magic-link/finalize', { code, did });
}

async function deleteBlockedSwitchAccountOption(code, did) {
  return adapter.post('/auth/manager-magic-link/options/delete', { code, did });
}

async function exchangeSubInviteCode(code) {
  return adapter.post('/auth/magic-link/sub-exchange', { code });
}

async function sendSubManagerInvitation(subIssuers, locale) {
  return adapter.post('/auth/sub-manager-invite', { subIssuers, locale });
}

async function cancelSubManagerInvitation(did) {
  return adapter.post('/auth/sub-manager-invite/cancel', { did });
}

async function resendSubManagerInvitation(did, locale) {
  return adapter.post('/auth/sub-manager-invite/resend', { did, locale });
}

async function getSwitchAccountToken() {
  return adapter.get('/auth/switch-account-token');
}

async function getSwitchAccountOptions() {
  return adapter.get('/auth/switch-account-options');
}

async function switchAccount(body) {
  return adapter.post('/auth/switch-account', body);
}

async function createSignupToken(body) {
  return adapter.post('/auth/signup-token', body);
}

async function createSignupTokenFromMultiAuth(code) {
  return adapter.post('/auth/signup-token-multi-auth', { code });
}

async function getIssuerAdmins() {
  return adapter.get('/managers/admins');
}

async function updateIssuerAdmin(body, options = {}) {
  const { file, removeProfileImage } = options;
  if (file || removeProfileImage) {
    const formData = new FormData();
    formData.append('oldEmail', body.oldEmail);
    formData.append('newEmail', body.newEmail);
    formData.append('firstName', body.firstName ?? '');
    formData.append('lastName', body.lastName ?? '');
    if (file) {
      formData.append('file', file);
    }
    if (removeProfileImage) {
      formData.append('removeProfileImage', 'true');
    }
    return adapter.put('/managers/admins', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }
  return adapter.put('/managers/admins', body);
}

async function addIssuerAdmin(body) {
  return adapter.post('/managers/admins', body);
}

async function validateAdminInviteCode(code) {
  return adapter.post('/auth/magic-link/admin-validate', { code });
}

async function deleteIssuerAdmin(body) {
  return adapter.post(`/managers/admins/delete`, body);
}

async function confirmIssuerAdminEmail(body) {
  return adapter.put('/managers/admins/confirm-email', body);
}

async function changeAdmin(body) {
  return adapter.post('/managers/admins/change', body);
}

async function verifyCaptcha(body) {
  return adapter.post('/auth/captcha', body);
}

async function getPointRanking(issuerDid) {
  return adapter.get(`/points/ranking/${issuerDid}`);
}

async function getCommunityMembers(pageNumber, pageSize, search, sort, order) {
  return adapter.get(`/managers/members`, {
    params: {
      pageNumber,
      pageSize,
      search: encodeURIComponent(search),
      sort,
      order,
    },
  });
}

/** Classement points / XP (optionnel : startDate + endDate ISO pour une période) — même logique que l’API communauté. */
async function getCommunityMembersRanking(params) {
  return adapter.get(`/managers/members/ranking`, { params });
}

async function getAllCommunityMembers() {
  return adapter.get(`/managers/members/all`);
}

async function publishToChannel(body, file) {
  const formData = new FormData();

  if (file) {
    formData.append('file', file);
  }

  for (const key in body) {
    if (body[key] !== null) {
      formData.append(key, body[key]);
    }
  }

  return adapter.post(`/managers/channel`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

async function getChannelMessages(size, beforeDate) {
  return adapter.get('/managers/channel', {
    params: {
      size,
      beforeDate,
    },
  });
}

async function deleteMessage(eventId) {
  return adapter.delete(`/managers/channel/${eventId}`);
}

async function regenerateApiKey() {
  return adapter.post('/managers/regenerate-api-key');
}

async function activateWeb2(body) {
  return adapter.post('/managers/activate-web2', body);
}

async function generateTemplateFromAi(body) {
  return adapter.post(`/managers/templates/ai`, body);
}

async function getApiVersion() {
  return adapter.get('/status/version');
}

async function getMentionsAnalytics(brandId, startDate, endDate, isFirstCall = true) {
  return getMentionsAnalyticsFromAnalyzer(brandId, startDate, endDate, isFirstCall);
}

async function getSubscriptionProduct(productId, { priceOnly = false } = {}) {
  const params = priceOnly ? '?priceOnly=true' : '';
  return adapter.get(`/subscription/product/${productId}${params}`);
}

async function createSubscription(body) {
  return adapter.post('/subscription/create', body);
}

async function confirmGrowthTrialPlan(body) {
  return adapter.post('/subscription/confirm-trial', body);
}

async function createCryptoSubscription(body) {
  return adapter.post('/subscription/create/crypto', body);
}

async function checkCryptoSubscription() {
  return adapter.post('/subscription/check/crypto');
}

async function getCryptoSubscriptionInfo() {
  return adapter.get('/subscription/crypto');
}

async function getCryptoSubscriptionPrices() {
  return adapter.get('/subscription/crypto/prices');
}

async function previewSubscriptionUpgrade(body) {
  return adapter.post('/subscription/preview-upgrade', body);
}

async function upgradeSubscription(body) {
  return adapter.put('/subscription/upgrade', body);
}

async function previewSubscriptionDowngrade(body) {
  return adapter.post('/subscription/preview-downgrade', body);
}

async function downgradeSubscription(body) {
  return adapter.put('/subscription/downgrade', body);
}

async function previewPeriodChange(body) {
  return adapter.post('/subscription/preview-period-change', body);
}

async function changeSubscriptionPeriod(body) {
  return adapter.put('/subscription/change-period', body);
}

async function previewSubscriptionCancellation() {
  return adapter.post('/subscription/preview-cancellation');
}

async function updateCryptoSubscriptionTier(body) {
  return adapter.put('/subscription/crypto/plan', body);
}

async function switchToFreeTier() {
  return adapter.post('/subscription/switch-to-free');
}

async function cancelScheduledCancellation() {
  return adapter.post('/subscription/cancel-scheduled-cancellation');
}

async function cancelScheduledDowngrade() {
  return adapter.post('/subscription/cancel-scheduled-downgrade');
}

async function createSubscriptionPortalSession() {
  return adapter.post('/subscription/portal');
}

async function getCheckoutSessionSummary(sessionId) {
  return adapter.get(`/subscription/checkout-session/${sessionId}`);
}

async function resetSubscriptionForTesting(body = {}) {
  return adapter.post('/subscription/test/reset', body);
}

async function redeemAccessCode(body) {
  return adapter.post('/subscription/redeem-access-code', body);
}

async function triggerHideMembers(state) {
  return adapter.post('/managers/hide-members', { state });
}

async function switchBanStatus(did) {
  return adapter.post('/managers/ban/switch', { did });
}

async function createBadgeForCommunityMember(body) {
  return adapter.post('/managers/members/badge', body);
}

export default {
  install(app) {
    const sseHandlers = [];
    let evtSource = null;

    let api = {
      getCitiesFromQuery(countryCode, query) {
        return adapter.get(`/cities/query/${countryCode}/${query}`);
      },
      getCityFromDetails(country, name) {
        return adapter.get(`/cities/details/${country}/${name}`);
      },
      createAuthChallenge(body) {
        return adapter.post('/auth/', body);
      },
      getAuthChallenge(challengeId) {
        return adapter.get(`/auth/${challengeId}`);
      },
      submitAuthVP(body) {
        return adapter.post('/auth/vp/', body);
      },
      sendIssuerPromoteRequest(body) {
        return adapter.post('/managers/promote', body);
      },
      updatePromoteAllowedDomains(allowedEmailDomains) {
        return adapter.put('/managers/promote-allowed-domains', { allowedEmailDomains });
      },
      getPromoteOAuthLink(provider) {
        return adapter.get('/managers/promote-verification/oauth-link', { params: { provider } });
      },
      getEmailVerification() {
        return adapter.get(`/managers/verifications/email`);
      },
      createEmailVerification(body) {
        return adapter.post('/managers/verifications/email', body);
      },
      submitEmailValidation(body) {
        return adapter.put('/managers/verifications/email', body);
      },
      waitChallengeValidation(challenge, next) {
        sseHandlers[challenge] = new EventSource(
          APIBaseURL + '/auth/sse/' + challenge,
          {
            xhrHeaders: {
              'Content-Type': 'text/event-stream',
              Connection: 'keep-alive',
            },
          }
        );

        sseHandlers[challenge].addEventListener(
          'message',
          (event) => {
            const response = JSON.parse(event.data).data;
            if (
              response.status == 'validated' &&
              response.challenge == challenge
            ) {
              sseHandlers[challenge].close();
              return next(response);
            } else if (response.status == 'expired') {
              sseHandlers[challenge].close();
              return next(response);
            } else {
              next(response);
            }
          },
          false
        );

        sseHandlers[challenge].addEventListener(
          'error',
          (event) => {
            if (event.readyState === EventSource.CLOSED) {
              return;
            }
          },
          false
        );
        return sseHandlers[challenge];
      },
      waitOperationValidation(operationId, next) {
        if (!operationId) {
          next({ status: 'error' });
          return null;
        }
        sseHandlers[operationId] = new EventSource(
          APIBaseURL + '/operations/sse/' + operationId,
          {
            xhrHeaders: {
              'Content-Type': 'text/event-stream',
              Connection: 'keep-alive',
            },
          }
        );

        sseHandlers[operationId].addEventListener(
          'message',
          (event) => {
            const response = JSON.parse(event.data);
            next(response);
            if (['success', 'error', 'canceled'].includes(response.status)) {
              sseHandlers[operationId].close();
              return;
            }
          },

          false
        );

        sseHandlers[operationId].addEventListener(
          'error',
          () => {
            if (sseHandlers[operationId]?.readyState === EventSource.CLOSED) {
              return;
            }
            sseHandlers[operationId]?.close();
            next({ status: 'error' });
          },
          false
        );
        return sseHandlers[operationId];
      },
      updateIssuer,
      createIssuer,
      deleteIssuer,
      getIssuer,
      getSubIssuers,
      isIssuerRegistered,
      switchSubIssuer,
      getIssuerTemplates,
      getIssuerTypeTemplates,
      getIssuerTemplate,
      updateTemplate,
      createTemplate,
      deleteTemplate,
      publishTemplate,
      deactivateTemplate,
      updateTemplateStatus,
      createSession,
      sendOpenSessionByEmail,
      getSendStatus,
      getEmailRateLimitStatus,
      updateSessionNote,
      createSessionBuilder,
      retriggerExpiredSession,
      getCommunityChallenges,
      createCommunityChallenge,
      updateCommunityChallenge,
      deleteCommunityChallenge,
      getCommunityQuests,
      getQuestChallenges,
      getQuestParticipants,
      createCommunityQuest,
      updateCommunityQuest,
      deleteCommunityQuest,
      logout,
      activateSession,
      promoteSession,
      adminRestore,
      sendManagerMagicAuth,
      leadBrandAudit,
      getLeadBrandAuditStatus,
      subscribeNewsletter,
      getBlogArticles,
      getBlogArticle,
      getBlogArticleBySlug,
      getPressPage,
      resendExpiredManagerMagicAuth,
      verifyManagerMagicAuth,
      getPasskeyLoginOptions,
      verifyPasskeyLogin,
      getPasskeyRegisterOptions,
      verifyPasskeyRegister,
      getPasskeyCredentials,
      deletePasskeyCredential,
      updatePasskeyCredentialName,
      finalizeMagicAuth,
      deleteBlockedSwitchAccountOption,
      exchangeSubInviteCode,
      sendSubManagerInvitation,
      cancelSubManagerInvitation,
      resendSubManagerInvitation,
      getSwitchAccountToken,
      getSwitchAccountOptions,
      switchAccount,
      createSignupToken,
      createSignupTokenFromMultiAuth,
      getIssuerAdmins,
      updateIssuerAdmin,
      addIssuerAdmin,
      deleteIssuerAdmin,
      confirmIssuerAdminEmail,
      changeAdmin,
      verifyCaptcha,
      getPointRanking,
      getCommunityMembers,
      getCommunityMembersRanking,
      getAllCommunityMembers,
      publishToChannel,
      getChannelMessages,
      deleteMessage,
      regenerateApiKey,
      getApiVersion,
      activateWeb2,
      generateTemplateFromAi,
      getSubscriptionProduct,
      createSubscription,
      confirmGrowthTrialPlan,
      createCryptoSubscription,
      checkCryptoSubscription,
      getCryptoSubscriptionInfo,
      getCryptoSubscriptionPrices,
      previewSubscriptionUpgrade,
      upgradeSubscription,
      previewSubscriptionDowngrade,
      downgradeSubscription,
      previewPeriodChange,
      changeSubscriptionPeriod,
      previewSubscriptionCancellation,
      updateCryptoSubscriptionTier,
      switchToFreeTier,
      cancelScheduledCancellation,
      cancelScheduledDowngrade,
      createSubscriptionPortalSession,
      getCheckoutSessionSummary,
      resetSubscriptionForTesting,
      redeemAccessCode,
      triggerHideMembers,
      switchBanStatus,
      createBadgeForCommunityMember,
      switchIssuerFromPrekey,
      getMentionsAnalytics,
      validateAdminInviteCode,
      /** Code retourné (200 ou 400) lorsque l'utilisateur refuse la signature dans l'app (Web3). Ne pas afficher de toast erreur. */
      SIGNATURE_REFUSED_CODE: 1000,
      isSignatureRefusedError(err) {
        return err?.response?.data?.code === 1000;
      },
      isSignatureRefusedResponse(data) {
        return data && data.success === false && data.code === 1000;
      },
      getLegalContent(type, locale) {
        return adapter.get(`/legal/${type}/${locale}`);
      },
    };
    app.provide('api', api);
  },
};

export {
  getIssuer,
  getSubIssuers,
  isIssuerRegistered,
  deleteIssuer,
  getIssuerTemplates,
  getIssuerTypeTemplates,
  getIssuerTemplate,
  updateTemplate,
  createTemplate,
  deleteTemplate,
  publishTemplate,
  deactivateTemplate,
  updateTemplateStatus,
  createSession,
  sendOpenSessionByEmail,
  getSendStatus,
  getEmailRateLimitStatus,
  updateSessionNote,
  createSessionBuilder,
  retriggerExpiredSession,
  getSubscriptionProduct,
  createSubscription,
  confirmGrowthTrialPlan,
  createCryptoSubscription,
  checkCryptoSubscription,
  getCryptoSubscriptionInfo,
  getCryptoSubscriptionPrices,
  previewSubscriptionUpgrade,
  upgradeSubscription,
  previewSubscriptionDowngrade,
  downgradeSubscription,
  previewPeriodChange,
  changeSubscriptionPeriod,
  previewSubscriptionCancellation,
  updateCryptoSubscriptionTier,
  switchToFreeTier,
  cancelScheduledCancellation,
  cancelScheduledDowngrade,
  createSubscriptionPortalSession,
  getCheckoutSessionSummary,
  resetSubscriptionForTesting,
  redeemAccessCode,
  switchIssuerFromPrekey,
  getMentionsAnalytics,
  validateAdminInviteCode,
  resendExpiredManagerMagicAuth,
};
